import { FormattedRequestObject, ProviderUsageResult, RequestInformation } from "@/lib/types"
import { Model } from "@/lib/types/model-types"
import { models } from "@/lib/models/model-list"
import type { ModelId } from "@/lib/models/model-list"
import { PricingCore } from "./pricing-core"
import { calculatePerplexityUsage } from "./calculate-perplexity-usage"
import { calculateOpenrouterUsage } from "./calculate-openrouter-usage"
import { calculateFalUsage } from "./calculate-fal-usage"
import { getModelTier, MODEL_TIERS } from "@/lib/models/model-tiers"

// Re-export core types for consumers
export * from "./pricing-core"

/**
 * FACADE: estimateRequestCost
 * Maintains compatibility with existing estimate-request-cost.ts
 */
export async function estimateRequestCost(generationData: FormattedRequestObject): Promise<number> {
    const model = models[generationData.inputObject.modelId]
    
    if (!model) {
        return 0
    }
    
    // Build text content from messages
    const textContent = (() => {
        let total = ""
        for (const message of generationData.inputObject.messages) {
            if (typeof message.content === "string") total += message.content + "\n"
            else if (Array.isArray(message.content)) {
                for (const part of message.content) {
                    if (part.type === "text" && "text" in part) total += part.text + "\n"
                }
            }
        }
        return total
    })()
    
    // Use core pricing engine
    const result = PricingCore.calculate({
        model,
        textContent,
        applyProfitMultiplier: true
    })
    
    // Add estimated output cost for image generation models
    if (model.pricing.output_image || model.pricing.output_image_mp) {
        const assumedImageCount = 1
        
        let additionalCost = 0
        if (model.pricing.output_image_mp) {
            // Assume 1 megapixel per image
            const megapixelsPerImage = 1
            additionalCost = megapixelsPerImage * assumedImageCount * model.pricing.output_image_mp
        } else if (model.pricing.output_image) {
            additionalCost = assumedImageCount * model.pricing.output_image
        }
        
        return result.totalCost + additionalCost
    }
    
    return result.totalCost
}

/**
 * FACADE: calculateUsage (from helper-generate.ts)
 * Maintains compatibility with existing calculateUsage in helper-generate.ts
 */
export function calculateUsage(usage: any, responseAPI: any, modelId: ModelId) {
    const model = models[modelId]
    
    if (!model) {
        return usage
    }
    
    // Use core pricing engine
    const result = PricingCore.calculate({
        model,
        apiResponse: responseAPI,
        applyProfitMultiplier: false // Original function doesn't apply profit multiplier
    })
    
    // Update the usage object with calculated values
    usage.inputTokens = result.inputTokens
    usage.outputTokens = result.outputTokens
    usage.totalTokens = result.totalTokens
    usage.inputCost = result.inputCost
    usage.outputCost = result.outputCost
    usage.totalCost = result.totalCost
    usage.inputMegapixels = result.inputMegapixels
    usage.outputMegapixels = result.outputMegapixels
    usage.totalMegapixels = result.totalMegapixels
    
    return usage
}

/**
 * FACADE: calculateUsage (from usage-calculator.ts)
 * Maintains compatibility with existing calculateUsage in usage-calculator.ts
 * Renamed to avoid conflict with the above function
 */
export function calculateUsageFromCredits(
    modelId: ModelId,
    credits: number
): { count: number, unit: 'messages' | 'images' | 'unknown' } | null {
    const model = models[modelId]
    
    if (!model) {
        return null
    }
    
    return PricingCore.estimateUsageFromCredits(model, credits)
}

/**
 * FACADE: estimateTokenUsage
 * Maintains compatibility with existing function
 */
export function estimateTokenUsage(text: string, modelId: string = "gpt-4"): number {
    return PricingCore.estimateTokenUsage(text, modelId)
}

/**
 * FACADE: estimateMaxOutputTokens
 * Maintains compatibility with existing function
 */
export function estimateMaxOutputTokens(
    userBalance: number,
    estimatedInputCost: number,
    model: Model
): number {
    return PricingCore.calculateMaxOutputTokens(userBalance, estimatedInputCost, model)
}

/**
 * FACADE: checkSufficientBalance
 * Maintains compatibility with existing function
 */
export async function checkSufficientBalance(estimatedCost: number, userData: any): Promise<boolean> {
    if (!userData?.balance) return true // Skip check if balance info not available
    
    return PricingCore.checkSufficientBalance(estimatedCost, userData.balance)
}

/**
 * FACADE: isExpensiveModel
 * Maintains compatibility with existing function
 */
export function isExpensiveModel(model: Model): boolean {
    if (!model) return false
    
    const tier = getModelTier(model)
    return tier === MODEL_TIERS.PREMIUM || tier === MODEL_TIERS.STANDARD
}

/**
 * ENHANCED: Unified pricing calculation
 * New function that provides access to all pricing calculations in one place
 */
export function calculatePricing(
    modelId: ModelId,
    options: {
        textContent?: string
        usage?: Partial<{
            inputTokens: number
            outputTokens: number
            inputMegapixels: number
            outputMegapixels: number
            imageCount: number
        }>
        apiResponse?: any
        applyProfitMultiplier?: boolean
    } = {}
) {
    const model = models[modelId]
    
    if (!model) {
        throw new Error(`Model not found: ${modelId}`)
    }
    
    return PricingCore.calculate({
        model,
        ...options
    })
}

// No helper re-exports; use PricingCore static helpers instead








export async function calculateProviderUsage(model: Model, responseAPI: RequestInformation['responseAPI']): Promise<ProviderUsageResult> {
    if (model.provider === 'perplexity') {
        return await calculatePerplexityUsage(responseAPI, model)
    }
    if (model.provider === 'openrouter') {
        return await calculateOpenrouterUsage({
            responseAPI,
            model,
        })
    }
    if (model.provider === 'fal') {
        return await calculateFalUsage({
            responseAPI,
            model,
        })
    }
    throw new Error(`Provider not supported: ${model.provider}`)
}

